export interface AdminApiClientOptions {
  baseUrl: string;
  tenantId: string;
  userId?: string;
  sessionToken?: string;
  fetcher?: typeof fetch;
}

export interface SharedCodeSession {
  userId: string;
  tenantId: string;
  sessionToken: string;
  expiresInSeconds: number;
}

export type AdminRequestContext = 'request' | 'upload';

export interface DealContentItemInput {
  name: string;
  quantity: number | string;
  unit: string;
  note?: string;
}

export interface DealContentGroupInput {
  name: string;
  items: DealContentItemInput[];
}

export interface PurchaseRulesInput {
  validity: string;
  availableTime: string;
  reservation: string;
  usage: string;
  restrictions?: string;
  refund: string;
  other?: string;
}

export interface PackageDetailInput {
  contentGroups?: DealContentGroupInput[];
  purchaseRules?: PurchaseRulesInput;
  validityDays?: number;
}

export class AdminApiError extends Error {
  readonly code: string | undefined;

  constructor(code: string | undefined, message: string) {
    super(message);
    this.code = code;
    this.name = 'AdminApiError';
  }
}

const NETWORK_ERROR_MESSAGE = '网络连接失败，请检查网络后重试';
const UPLOAD_ERROR_MESSAGE = '图片上传失败，请检查网络或稍后重试';
const GENERIC_ERROR_MESSAGE = '服务暂时不可用，请稍后重试';

const ERROR_MESSAGES: Record<string, string> = {
  TENANT_NOT_FOUND: '门店暂未完成配置，请稍后再试',
  UNAUTHORIZED: '登录已失效，请重新登录',
  SESSION_EXPIRED: '登录已失效，请重新登录',
  SESSION_INVALID: '登录已失效，请重新登录',
  SHARED_CODE_INVALID: '统一口令不正确，请重新输入',
  SHARED_AUTH_NOT_CONFIGURED: '后台登录尚未配置，请联系管理员',
  SHARED_CODE_RATE_LIMITED: '尝试次数过多，请稍后再试',
  TENANT_SCOPE: '当前登录门店不匹配，请联系管理员',
  PACKAGE_DELETE_NOT_ALLOWED: '该套餐已有上架或交易记录，请改为归档',
  PACKAGE_PUBLISH_REQUIREMENTS: '请补齐套餐主图、内容清单、有效天数和购买须知后再上架',
  INVALID_VALIDITY_DAYS: '套餐有效天数需在 1-28 天之间（受微信支付 30 天冻结期约束）',
  LEGACY_PACKAGE_THUMBNAIL_REFRESH_REQUIRED: '套餐图片规格已升级，请刷新后台后重新选择图片',
  NO_REFUNDABLE_VOUCHERS: '该订单当前没有可退款的未核销券',
  INVALID_ORDER_RESPONSE: '订单服务返回的数据格式异常，请重新加载',
  ORDER_DATA_INVALID: '订单数据异常，页面已使用安全默认值展示',
  INVALID_COORDINATE: '请输入有效的门店坐标',
};

export function getAdminErrorMessage(error: unknown, context: AdminRequestContext = 'request'): string {
  const code = error instanceof AdminApiError ? error.code : undefined;
  const rawMessage = error instanceof Error ? error.message : String(error ?? '');
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
  if (context === 'upload') return UPLOAD_ERROR_MESSAGE;
  if (code === 'NETWORK_ERROR' || /failed to fetch|fetch failed|illegal invocation|networkerror|network request/i.test(rawMessage)) {
    return NETWORK_ERROR_MESSAGE;
  }
  return GENERIC_ERROR_MESSAGE;
}

function asAdminError(error: unknown, context: AdminRequestContext): AdminApiError {
  if (error instanceof AdminApiError) return error;
  const rawMessage = error instanceof Error ? error.message : String(error ?? '');
  const code = /^HTTP_\d+$/.test(rawMessage) || /^[A-Z][A-Z0-9_]+$/.test(rawMessage) ? rawMessage : undefined;
  return new AdminApiError(code, getAdminErrorMessage(new AdminApiError(code, rawMessage), context));
}

async function readPayload(response: Response): Promise<Record<string, unknown>> {
  try {
    const payload = await response.json();
    return payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function responseError(payload: Record<string, unknown>, status: number, context: AdminRequestContext): AdminApiError {
  const code = typeof payload.code === 'string' ? payload.code : `HTTP_${status}`;
  return new AdminApiError(code, getAdminErrorMessage(new AdminApiError(code, code), context));
}

export async function loginWithSharedCode(baseUrl: string, code: string, fetcher: typeof fetch = fetch): Promise<SharedCodeSession> {
  try {
    const response = await fetcher.call(globalThis, `${baseUrl.replace(/\/$/, '')}/v1/auth/shared-code`, {
      method: 'POST',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    const payload = await readPayload(response);
    if (!response.ok) throw responseError(payload, response.status, 'request');
    return payload as unknown as SharedCodeSession;
  } catch (error) {
    throw asAdminError(error, 'request');
  }
}

export class AdminApiClient {
  private readonly baseUrl: string;
  private readonly tenantId: string;
  private readonly userId?: string;
  private sessionToken?: string;
  private readonly fetcher: typeof fetch;

  constructor(options: AdminApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, '');
    this.tenantId = options.tenantId;
    this.userId = options.userId;
    this.sessionToken = options.sessionToken;
    this.fetcher = options.fetcher ?? fetch;
  }

  setSessionToken(sessionToken: string | undefined): void {
    this.sessionToken = sessionToken;
  }

  listPackages() { return this.get(`/v1/tenants/${encodeURIComponent(this.tenantId)}/packages`); }
  listManagePackages() { return this.get(`/v1/tenants/${encodeURIComponent(this.tenantId)}/packages/manage`); }
  listOrders() { return this.get(`/v1/tenants/${encodeURIComponent(this.tenantId)}/orders`); }
  listCustomerOrders(status?: string) { return this.get(`/v1/tenants/${encodeURIComponent(this.tenantId)}/me/orders${status ? `?status=${encodeURIComponent(status)}` : ''}`); }
  listAuditLogs() { return this.get(`/v1/tenants/${encodeURIComponent(this.tenantId)}/audit-logs`); }
  getReport() { return this.get(`/v1/tenants/${encodeURIComponent(this.tenantId)}/report`); }
  getIntegrationStatus() { return this.get(`/v1/tenants/${encodeURIComponent(this.tenantId)}/integration-status`); }
  getStoreProfile() { return this.get(`/v1/tenants/${encodeURIComponent(this.tenantId)}/store-profile`); }

  updateStoreProfile(input: Record<string, unknown>) {
    return this.request(`/v1/tenants/${encodeURIComponent(this.tenantId)}/store-profile`, 'PUT', input);
  }

  uploadMedia(file: Blob, filename: string, purpose: 'store-cover' | 'store-logo' | 'package-cover') {
    const form = new FormData();
    form.append('purpose', purpose);
    form.append('file', file, filename);
    return this.requestForm(`/v1/tenants/${encodeURIComponent(this.tenantId)}/media`, form);
  }

  createPackage(input: { name: string; description: string; coverUrl?: string; priceFen: number; validUntil: string; status?: 'DRAFT' | 'PUBLISHED' | 'OFF_SHELF' } & PackageDetailInput) {
    return this.post(`/v1/tenants/${encodeURIComponent(this.tenantId)}/packages`, input);
  }

  updatePackageStatus(packageId: string, status: 'DRAFT' | 'PUBLISHED' | 'OFF_SHELF') {
    return this.post(`/v1/tenants/${encodeURIComponent(this.tenantId)}/packages/${encodeURIComponent(packageId)}/status`, { status });
  }

  updatePackage(packageId: string, input: { name?: string; description?: string; coverUrl?: string | null; priceFen?: number; validUntil?: string; contentGroups?: DealContentGroupInput[] | null; purchaseRules?: PurchaseRulesInput | null; validityDays?: number }) {
    return this.request(`/v1/tenants/${encodeURIComponent(this.tenantId)}/packages/${encodeURIComponent(packageId)}`, 'PUT', input);
  }

  deletePackage(packageId: string) {
    return this.request(`/v1/tenants/${encodeURIComponent(this.tenantId)}/packages/${encodeURIComponent(packageId)}`, 'DELETE');
  }

  archivePackage(packageId: string) {
    return this.post(`/v1/tenants/${encodeURIComponent(this.tenantId)}/packages/${encodeURIComponent(packageId)}/archive`, {});
  }

  restorePackage(packageId: string) {
    return this.post(`/v1/tenants/${encodeURIComponent(this.tenantId)}/packages/${encodeURIComponent(packageId)}/restore`, {});
  }

  getOrder(orderId: string) { return this.get(`/v1/orders/${encodeURIComponent(orderId)}`); }
  listOrderVouchers(orderId: string) { return this.get(`/v1/orders/${encodeURIComponent(orderId)}/vouchers`); }

  refundOrder(orderId: string) { return this.post(`/v1/orders/${encodeURIComponent(orderId)}/refund`, {}); }

  private async get(path: string): Promise<unknown> { return this.request(path, 'GET'); }

  private async post(path: string, body: unknown): Promise<unknown> { return this.request(path, 'POST', body); }

  private async request(path: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE', body?: unknown): Promise<unknown> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (this.sessionToken) headers['x-session-token'] = this.sessionToken;
    if (body !== undefined) headers['content-type'] = 'application/json';
    try {
      const response = await this.fetcher.call(globalThis, `${this.baseUrl}${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
      const payload = await readPayload(response);
      if (!response.ok) throw responseError(payload, response.status, 'request');
      return payload;
    } catch (error) {
      throw asAdminError(error, 'request');
    }
  }

  private async requestForm(path: string, body: FormData): Promise<unknown> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (this.sessionToken) headers['x-session-token'] = this.sessionToken;
    try {
      const response = await this.fetcher.call(globalThis, `${this.baseUrl}${path}`, { method: 'POST', headers, body });
      const payload = await readPayload(response);
      if (!response.ok) throw responseError(payload, response.status, 'upload');
      return payload;
    } catch (error) {
      throw asAdminError(error, 'upload');
    }
  }
}
